import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';

const ACCENT = '#10B981';
const AMBER = '#F59E0B';
const RED = '#EF4444';
const ORANGE = '#F97316';

const CARD: React.CSSProperties = {
  background: 'var(--card)',
  border: '1px solid var(--border)',
  borderRadius: 20,
  padding: 28,
};

const APP_TYPES = ['Business', 'E-Commerce', 'Booking', 'Delivery', 'Portfolio', 'Community'] as const;
const PLATFORMS = ['Android', 'iOS', 'Both (Recommended)'] as const;

const FEATURE_ITEMS = [
  { id: 'push', label: '📱 Push Notifications' },
  { id: 'chat', label: '💬 In-App Chat' },
  { id: 'catalog', label: '🛒 Product Catalog' },
  { id: 'pay', label: '💳 Payments' },
  { id: 'gps', label: '📍 GPS Tracking' },
  { id: 'reviews', label: '⭐ Reviews' },
  { id: 'book', label: '📅 Booking System' },
  { id: 'loyalty', label: '🎁 Loyalty Points' },
  { id: 'analytics', label: '📊 Analytics' },
  { id: 'orders', label: '🔔 Order Tracking' },
  { id: 'i18n', label: '🌐 Multi-Language' },
  { id: 'aibot', label: '🤖 AI Chatbot' },
] as const;

const SWATCHES = ['#10B981', '#6366F1', '#F97316', '#EF4444', '#06B6D4', '#8B5CF6'] as const;
const ICON_STYLES = ['🏠 Clean', '⚡ Bold', '🌿 Minimal', '🎨 Colorful'] as const;
const SPLASH_STYLES = ['Dark', 'Light', 'Brand Color'] as const;

const SHOWCASE = [
  { icon: '🤖', title: 'AI-Powered', body: 'Full ODC AI agent suite works inside your app' },
  { icon: '📲', title: 'Push Notifications', body: 'Send offers, updates, reminders directly to phones' },
  { icon: '💳', title: 'In-App Payments', body: 'UPI, cards, wallets — all payment methods' },
  { icon: '📍', title: 'Location Features', body: 'GPS tracking, nearby stores, location-based offers' },
  { icon: '🔄', title: 'Auto-Updates', body: 'App content updates automatically from ODC dashboard' },
  { icon: '📊', title: 'App Analytics', body: 'User behavior, sessions, conversions — all tracked' },
] as const;

export function AppBuilderPage() {
  const [step, setStep] = useState(1);
  const [appName, setAppName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [appType, setAppType] = useState('');
  const [platform, setPlatform] = useState('Both (Recommended)');
  const [features, setFeatures] = useState<string[]>([]);
  const [color, setColor] = useState('#10B981');
  const [iconStyle, setIconStyle] = useState('🏠 Clean');
  const [splashStyle, setSplashStyle] = useState('Dark');
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [barKey, setBarKey] = useState(0);
  const [e1, setE1] = useState(false);
  const [e2, setE2] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const { data } = await api.get<{ business: { business_name: string } | null }>('/business/me');
        if (data.business?.business_name) setBusinessName(data.business.business_name);
      } catch {
        /* demo */
      }
    })();
  }, []);

  useEffect(() => {
    if (!generating) return;
    const t = window.setTimeout(() => {
      setGenerated(true);
      setGenerating(false);
    }, 4000);
    return () => window.clearTimeout(t);
  }, [generating]);

  function toggleFeat(id: string) {
    setFeatures((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id]));
  }

  function startGen() {
    setBarKey((k) => k + 1);
    setGenerating(true);
  }

  const pillOrange = (on: boolean): React.CSSProperties => ({
    border: `1px solid ${on ? ORANGE : 'var(--border)'}`,
    borderRadius: 999,
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    background: on ? 'rgba(249,115,22,0.1)' : 'var(--card2)',
    color: on ? 'var(--text)' : 'var(--muted)',
  });

  const pillAccent = (on: boolean): React.CSSProperties => ({
    border: `1px solid ${on ? ACCENT : 'var(--border)'}`,
    borderRadius: 999,
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    background: on ? 'rgba(16,185,129,0.08)' : 'var(--card2)',
    color: on ? 'var(--text)' : 'var(--muted)',
  });

  return (
    <div className="odc-page relative z-10" style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
      <style>{`
        @keyframes odc-app-bar-fill { from { width: 0%; } to { width: 100%; } }
        .odc-app-bar-inner {
          height: 100%;
          border-radius: 2px;
          background: linear-gradient(90deg, ${ORANGE}, ${AMBER});
          animation: odc-app-bar-fill 4s linear forwards;
        }
        .odc-app-showcase { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
        @media (max-width: 900px) { .odc-app-showcase { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 560px) { .odc-app-showcase { grid-template-columns: 1fr !important; } }
        .odc-app-feat { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
        @media (max-width: 900px) { .odc-app-feat { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 480px) { .odc-app-feat { grid-template-columns: 1fr !important; } }
      `}</style>

      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 20, alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 280px', minWidth: 0 }}>
          <span
            style={{
              display: 'inline-flex',
              padding: '5px 14px',
              borderRadius: 999,
              border: '1px solid rgba(249,115,22,0.3)',
              background: 'rgba(249,115,22,0.08)',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.14em',
              color: ORANGE,
              textTransform: 'uppercase',
            }}
          >
            AI APP BUILDER · NEW
          </span>
          <h1 style={{ margin: '12px 0 0', fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', fontWeight: 800, color: 'var(--text)' }}>
            Build Your Mobile App with AI
          </h1>
          <p style={{ margin: '12px 0 0', color: 'var(--muted)', fontSize: 15, maxWidth: 560, lineHeight: 1.6 }}>
            Android + iOS app — generated in 5 minutes. No coding. No agency. Just ODC.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {['📱 Android + iOS', '⚡ 5 Min Build', '💰 No Agency Fees'].map((t) => (
            <span
              key={t}
              style={{
                border: '1px solid var(--border)',
                borderRadius: 999,
                padding: '8px 16px',
                background: 'var(--card)',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--text)',
              }}
            >
              {t}
            </span>
          ))}
        </div>
      </div>

      <div className="odc-card" style={{ ...CARD, transform: 'none', width: '100%' }}>
        <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>Why ODC App Builder?</h2>
        <div style={{ overflowX: 'auto' }}>
          <table className="odc-table" style={{ width: '100%', minWidth: 520 }}>
            <thead>
              <tr>
                <th />
                <th>Agency</th>
                <th>No-Code Tools</th>
                <th style={{ background: 'rgba(16,185,129,0.12)', color: ACCENT }}>ODC App Builder</th>
              </tr>
            </thead>
            <tbody>
              {(
                [
                  ['Cost', '₹2-10 Lakh', '₹5,000/mo', 'Included in Pro'],
                  ['Time', '3-6 months', '2-4 weeks', '5 Minutes'],
                  ['AI Integration', '❌', '❌', '✅ Full AI suite'],
                  ['ODC Connected', '❌', '❌', '✅ Auto-sync'],
                  ['Updates', 'Paid extra', 'Manual', '✅ Auto'],
                ] as const
              ).map(([a, b, c, d]) => (
                <tr key={a}>
                  <td style={{ fontWeight: 700 }}>{a}</td>
                  <td>{b}</td>
                  <td>{c}</td>
                  <td style={{ background: 'rgba(16,185,129,0.06)', fontWeight: 600, color: ACCENT }}>{d}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {!generated ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
            {([1, 2, 3] as const).map((n) => (
              <React.Fragment key={n}>
                {n > 1 ? (
                  <div
                    style={{
                      flex: '1 1 32px',
                      maxWidth: 72,
                      height: 2,
                      background: step >= n ? ORANGE : 'var(--border)',
                      margin: '0 6px',
                    }}
                  />
                ) : null}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, width: 88 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      fontSize: 14,
                      background: step > n ? 'rgba(249,115,22,0.2)' : step === n ? ORANGE : 'var(--card)',
                      border: step > n || step === n ? 'none' : '1px solid var(--border)',
                      color: step > n ? ORANGE : step === n ? '#fff' : 'var(--muted)',
                    }}
                  >
                    {step > n ? '✓' : n}
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textAlign: 'center' }}>
                    {n === 1 ? 'App Details' : n === 2 ? 'Features' : 'Design'}
                  </span>
                </div>
              </React.Fragment>
            ))}
          </div>

          <div className="odc-card" style={{ ...CARD, maxWidth: 720, margin: '0 auto', width: '100%', transform: 'none' }}>
            {step === 1 ? (
              <>
                <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>Tell us about your app</h2>
                <label style={{ display: 'block', marginBottom: 12 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', display: 'block', marginBottom: 6 }}>App Name</span>
                  <input
                    className="odc-input"
                    style={{ width: '100%' }}
                    value={appName}
                    onChange={(e) => {
                      setAppName(e.target.value);
                      setE1(false);
                    }}
                    placeholder="e.g. ABC Real Estate App"
                  />
                </label>
                <label style={{ display: 'block', marginBottom: 12 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', display: 'block', marginBottom: 6 }}>Business</span>
                  <input className="odc-input" style={{ width: '100%', opacity: 0.7 }} value={businessName} readOnly />
                </label>
                <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 8 }}>App Type</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                  {APP_TYPES.map((t) => (
                    <button key={t} type="button" onClick={() => { setAppType(t); setE1(false); }} style={pillOrange(appType === t)}>
                      {t}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 8 }}>Target Platform</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                  {PLATFORMS.map((p) => (
                    <button key={p} type="button" onClick={() => setPlatform(p)} style={pillOrange(platform === p)}>
                      {p}
                    </button>
                  ))}
                </div>
                {e1 ? <p style={{ color: RED, fontSize: 13, marginBottom: 8 }}>Please fill app name and app type</p> : null}
                <button
                  type="button"
                  className="odc-btn-primary"
                  style={{ width: '100%' }}
                  onClick={() => {
                    if (appName.trim() && appType) setStep(2);
                    else setE1(true);
                  }}
                >
                  Next →
                </button>
              </>
            ) : null}

            {step === 2 ? (
              <>
                <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>Select app features</h2>
                <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--muted)' }}>Choose what your app can do</p>
                <div className="odc-app-feat">
                  {FEATURE_ITEMS.map((f) => {
                    const on = features.includes(f.id);
                    return (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => {
                          toggleFeat(f.id);
                          setE2(false);
                        }}
                        style={{
                          border: `1px solid ${on ? ACCENT : 'var(--border)'}`,
                          borderRadius: 14,
                          padding: 16,
                          textAlign: 'center',
                          cursor: 'pointer',
                          background: on ? 'rgba(16,185,129,0.08)' : 'var(--card2)',
                          color: 'var(--text)',
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        {f.label}
                      </button>
                    );
                  })}
                </div>
                {e2 ? <p style={{ color: RED, fontSize: 13, marginTop: 12 }}>Select at least 2 features</p> : null}
                <button
                  type="button"
                  className="odc-btn-primary"
                  style={{ width: '100%', marginTop: 16 }}
                  onClick={() => {
                    if (features.length >= 2) setStep(3);
                    else setE2(true);
                  }}
                >
                  Next →
                </button>
              </>
            ) : null}

            {step === 3 ? (
              <>
                <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>Customize your app&apos;s look</h2>
                <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 10 }}>Brand Color</p>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                  {SWATCHES.map((c) => (
                    <button
                      key={c}
                      type="button"
                      aria-label={c}
                      onClick={() => setColor(c)}
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: c,
                        cursor: 'pointer',
                        border: color === c ? '3px solid #fff' : '2px solid var(--border)',
                        transform: color === c ? 'scale(1.2)' : 'scale(1)',
                        boxSizing: 'content-box',
                      }}
                    />
                  ))}
                </div>
                <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 8 }}>App Icon style</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                  {ICON_STYLES.map((s) => (
                    <button key={s} type="button" onClick={() => setIconStyle(s)} style={pillAccent(iconStyle === s)}>
                      {s}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 8 }}>Splash screen style</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                  {SPLASH_STYLES.map((s) => (
                    <button key={s} type="button" onClick={() => setSplashStyle(s)} style={pillAccent(splashStyle === s)}>
                      {s}
                    </button>
                  ))}
                </div>
                {generating ? (
                  <div style={{ width: '100%', height: 4, background: 'var(--border)', borderRadius: 2, marginBottom: 16, overflow: 'hidden' }}>
                    <div key={barKey} className="odc-app-bar-inner" />
                  </div>
                ) : null}
                <button
                  type="button"
                  disabled={generating}
                  onClick={startGen}
                  style={{
                    width: '100%',
                    background: 'linear-gradient(135deg, #F97316, #F59E0B)',
                    border: 'none',
                    color: '#000',
                    padding: 16,
                    borderRadius: 14,
                    fontSize: 16,
                    fontWeight: 700,
                    cursor: generating ? 'wait' : 'pointer',
                  }}
                >
                  {generating ? '⚡ Building your app...' : '📱 Generate My App'}
                </button>
              </>
            ) : null}
          </div>
        </>
      ) : (
        <div className="odc-card" style={{ ...CARD, maxWidth: 720, margin: '0 auto', width: '100%', textAlign: 'center', transform: 'none' }}>
          <div style={{ fontSize: 80, lineHeight: 1 }}>📱</div>
          <h2 style={{ margin: '16px 0 8px', fontSize: 22, fontWeight: 800, color: 'var(--text)' }}>Your App is Ready!</h2>
          <p style={{ margin: 0, color: 'var(--muted)', fontSize: 15 }}>
            {appName || 'Your app'} has been generated for Android &amp; iOS
          </p>
          <div
            style={{
              width: 200,
              height: 380,
              border: '8px solid #333',
              borderRadius: 36,
              margin: '24px auto 0',
              overflow: 'hidden',
              background: `linear-gradient(135deg, #020B18, ${color}33, #020B18)`,
              textAlign: 'left',
              padding: 10,
              color: 'rgba(255,255,255,0.92)',
            }}
          >
            <div style={{ height: 14, fontSize: 8, opacity: 0.6, textAlign: 'center' }}>● ● ●</div>
            <div style={{ fontSize: 10, fontWeight: 700, marginTop: 8, opacity: 0.9 }}>{businessName || 'Business'}</div>
            <div style={{ fontSize: 16, fontWeight: 800, marginTop: 12 }}>{appName || 'App'}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginTop: 14 }}>
              {['Home', 'Shop', 'Profile'].map((x) => (
                <div key={x} style={{ borderRadius: 6, padding: 8, background: 'rgba(255,255,255,0.08)', fontSize: 9, textAlign: 'center' }}>
                  {x}
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap', marginTop: 24 }}>
            <button type="button" className="odc-btn-primary" disabled title="Coming soon - Pro feature">
              📱 Download APK
            </button>
            <button type="button" className="odc-btn-ghost" disabled>
              🍎 Submit to App Store
            </button>
            <button
              type="button"
              className="odc-btn-ghost"
              onClick={() => {
                setGenerated(false);
                setStep(1);
              }}
            >
              ✏️ Edit &amp; Regenerate
            </button>
          </div>
          <p style={{ margin: '16px 0 0', fontSize: 12, color: 'var(--muted)', textAlign: 'center', lineHeight: 1.5 }}>
            Full app publishing available in Pro &amp; Enterprise plans. APK download and App Store submission coming Q3 2026.
          </p>
        </div>
      )}

      <div>
        <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: 'var(--text)', textAlign: 'center' }}>
          What Your App Can Do
        </h2>
        <p style={{ margin: '0 0 20px', fontSize: 14, color: 'var(--muted)', textAlign: 'center' }}>
          ODC App = Your website + ODC AI agents in your customer&apos;s pocket
        </p>
        <div className="odc-app-showcase">
          {SHOWCASE.map((s) => (
            <div key={s.title} style={{ ...CARD, padding: 22, transform: 'none' }}>
              <div style={{ fontSize: 28 }}>{s.icon}</div>
              <h3 style={{ margin: '10px 0 0', fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>{s.title}</h3>
              <p style={{ margin: '8px 0 0', fontSize: 14, color: 'var(--muted)', lineHeight: 1.55 }}>{s.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="odc-card" style={{ ...CARD, textAlign: 'center', transform: 'none' }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--text)' }}>📱 App Builder Pricing</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 16, marginTop: 24 }}>
          {(
            [
              ['Starter/Growth', 'Not included', '₹4,999/mo addon'],
              ['Pro', '1 App included', 'Android + iOS'],
              ['Enterprise/Agency', 'Unlimited Apps', 'White-label ready'],
            ] as const
          ).map(([a, b, c]) => (
            <div
              key={a}
              style={{
                flex: '1 1 200px',
                maxWidth: 260,
                border: '1px solid var(--border)',
                borderRadius: 16,
                padding: 20,
                background: 'var(--bg2)',
              }}
            >
              <p style={{ margin: 0, fontWeight: 800, fontSize: 13, color: ACCENT }}>{a}</p>
              <p style={{ margin: '8px 0 0', fontSize: 14, color: 'var(--text)', fontWeight: 600 }}>{b}</p>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--muted)' }}>{c}</p>
            </div>
          ))}
        </div>
        <Link to="/pricing" className="odc-btn-ghost" style={{ marginTop: 24, display: 'inline-flex', textDecoration: 'none' }}>
          View Plans →
        </Link>
      </div>
    </div>
  );
}
